import { Analytics } from '@util/Analytics';
import type { CoverageAddress } from 'hydra-data-types';
import React, { ReactElement } from 'react';
import { LayoutScreenConfig, ScreenType } from '../../../config/FlowTypes';
import { useAppStore } from '../../../store/Store';
import { dispatchUserData } from '../../../store/userData/UserDataActions';
import { getCoverageAddress, toCoverageAddressUserData } from '../../../util/CoverageAddress';
import { NavigationProps } from '../FlowNavigation';
import {
  CoverageAddressLayout,
  CoverageAddressLayoutConfig,
} from '../layouts/CoverageAddressLayout';

export type CoverageAddressScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.CoverageAddress,
  CoverageAddressLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  CoverageAddressScreenConfig<ScreenNames>,
  ScreenNames
>;

/** Analytics answer for the "I can't find my address" exit; the address itself is never sent. */
const NOT_FOUND_ANSWER = 'notFound';

export const CoverageAddressScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const prefill = useAppStore((state) => getCoverageAddress(state.userData));

  function onSubmit(address: CoverageAddress): void {
    dispatchUserData({
      ...toCoverageAddressUserData(address),
      // The Medicare MBI screen reads the single-line form, so keep it in step.
      residentialAddress: address.formattedAddress,
      coverageAddressNotFound: ['No'],
    });
    // A home address is PII — record that the question was answered, never the answer.
    Analytics.track({ event: Analytics.Event.o_question_answered, question: props.screenName });
    props.navigation.next(props);
  }

  function onNotFound(): void {
    dispatchUserData({ coverageAddressNotFound: ['Yes'] });
    Analytics.track({
      event: Analytics.Event.o_question_answered,
      question: props.screenName,
      answer: NOT_FOUND_ANSWER,
    });
    props.navigation.next(props);
  }

  return (
    <CoverageAddressLayout
      {...props.layout}
      prefill={prefill}
      onSubmit={onSubmit}
      onNotFound={onNotFound}
    />
  );
};
